import type { ReactNode } from 'react';

interface TimelineItem {
  /** short date label, e.g. "May 2025" */
  date: string;
  /** one short line of context for the milestone */
  title: string;
  /** render the dot and date in the brand accent (the key beats) */
  highlight?: boolean;
}

export interface TimelineProps {
  items: TimelineItem[];
  caption?: ReactNode;
  ariaLabel?: string;
}

/**
 * Horizontal milestone timeline: dates sit on a single rule with one line of
 * context each. Theme-aware (tokens only); scrolls horizontally on narrow screens.
 */
export const Timeline = ({ items, caption, ariaLabel = 'timeline' }: TimelineProps) => (
  <figure className="my-6">
    <div className="overflow-x-auto rounded-10 border border-line bg-surface-2 px-6 py-7">
      <ol className="flex min-w-165 list-none gap-0 p-0" aria-label={ariaLabel}>
        {items.map((it, i) => (
          <li key={i} className="flex flex-1 flex-col items-center text-center">
            <span
              className={`font-mono text-13 tracking-wide ${it.highlight ? 'font-semibold text-accent' : 'text-text-3'}`}
            >
              {it.date}
            </span>
            <span className="relative my-3 flex h-3 w-full items-center justify-center">
              <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line" />
              <span
                className={`relative h-3 w-3 rounded-full ${it.highlight ? 'bg-accent' : 'border-2 border-line bg-surface'}`}
              />
            </span>
            <span className="px-2 text-13 leading-snug text-text-2">{it.title}</span>
          </li>
        ))}
      </ol>
    </div>
    {caption ? (
      <figcaption className="mt-2 text-center text-13 text-text-3">{caption}</figcaption>
    ) : null}
  </figure>
);
